import { measureTextWidth } from '../shared/measureText';
import type { ComparisonBarLayout } from './types';

// ================================================================================================
// The HTML/canvas seam.
//
// This chart is a hybrid: ECharts draws the row labels, the grouped bars and the bar-end labels,
// while HTML draws the sortable header, the delta-chip column and the row-hover band. Those two
// have to agree on where every row sits to within a pixel, and they cannot negotiate — the canvas
// cannot be measured before it is drawn, and reading geometry back out of an ECharts instance ties
// the layout to render timing.
//
// So neither side measures the other. Both call this one pure function and lay themselves out from
// the same numbers.
// ================================================================================================

/** Thickness of a single bar. */
export const BAR_THICKNESS = 16;
/** Gap between two range bars within the same row. */
export const BAR_STACK_GAP = 6;
/** Vertical breathing room around a row's stack of bars. */
export const ROW_PADDING = 24;
export const GRID_TOP = 8;
/** Gap between a bar's tip and its amount label. */
export const BAR_LABEL_GAP = 8;

/** Minimum space reserved right of the plot, so a short label still has room. */
const MIN_END_LABEL_RESERVE = 72;
/** Font the bar-end labels render in — must match the label style in series.ts. */
const END_LABEL_FONT = '500 14px Rubik, sans-serif';
/**
 * Total horizontal padding around the y-axis label text: the card's 16px left padding plus the
 * 16px gutter between the label and the first bar. Split evenly, which is what lets the option
 * builder derive both `axisLabel.width` and `axisLabel.margin` from the column width alone.
 */
export const LABEL_INSET = 32;

/**
 * Height of one category band.
 *
 * `boundaryGap: true` splits `grid.height` into equal bands and centres each column group in its
 * band, so as long as `gridHeight` is an exact multiple of this there is no rounding drift and
 * band centre n is always at `GRID_TOP + (n + 0.5) * rowHeight`. The HTML chip column does that
 * same arithmetic, which is the whole alignment guarantee.
 */
export const rowHeightFor = (rangeCount: number): number => {
  const bars = Math.max(1, rangeCount);
  return bars * BAR_THICKNESS + (bars - 1) * BAR_STACK_GAP + ROW_PADDING;
};

export type ComparisonBarLayoutInput = {
  rowCount: number;
  rangeCount: number;
  labelWidthPx: number;
  deltaColumnPx: number;
  /** Every bar-end label that will render, for measuring the right margin. */
  endLabels?: string[];
};

export const buildComparisonBarLayout = ({
  rowCount,
  rangeCount,
  labelWidthPx,
  deltaColumnPx,
  endLabels = [],
}: ComparisonBarLayoutInput): ComparisonBarLayout => {
  const rowHeight = rowHeightFor(rangeCount);

  // Measured rather than estimated at N px/char: the $ , . glyphs skew a character average badly,
  // and this degrades to a character count outside a DOM rather than failing.
  const widestLabel = endLabels.reduce((widest, label) => (label.length > widest.length ? label : widest), '');
  const endLabelReserve = Math.max(
    MIN_END_LABEL_RESERVE,
    Math.ceil(measureTextWidth(widestLabel, END_LABEL_FONT)) + BAR_LABEL_GAP + 8,
  );

  return {
    rowHeight,
    // Explicit, never inferred from the canvas element's height. That is what lets the canvas be
    // over-tall inside a height-animating viewport — the expand/collapse transition — without the
    // rows resizing as it animates.
    gridHeight: Math.max(rowHeight, rowCount * rowHeight),
    gridTop: GRID_TOP,
    gridLeft: labelWidthPx,
    // Spans the chip column as well as the label gutter, so the plot's right edge is the chip
    // column's left edge and the HTML header needs no pixel maths of its own.
    gridRight: deltaColumnPx + endLabelReserve,
    endLabelReserve,
    barWidth: BAR_THICKNESS,
    // As a fraction of bar width, which is how ECharts reads it: 6/16 = 37.5%, giving exactly
    // BAR_STACK_GAP px between bars for the BAR_THICKNESS above.
    barGap: `${(BAR_STACK_GAP / BAR_THICKNESS) * 100}%`,
  };
};
